/**
 * Seccionado del modelo 3D: un plano horizontal corta la pieza para poder ver
 * su interior — el vaciado de una caja de luz, el grosor real de un llavero.
 *
 * Es solo una ayuda de visualización: no toca la geometría, ni el GLB, ni el
 * 3MF, ni nada de lo que se manda a fabricar. Vale para cualquier producto
 * porque solo mira la geometría, no los nombres de las mallas (a diferencia de
 * `partsLayout.js`, que sí distingue las piezas `front_*`).
 *
 * ## Por qué hay meshes de más
 *
 * `material.clippingPlanes` por sí solo deja el corte ABIERTO: como los
 * materiales solo pintan las caras frontales, al cortar se ve el interior
 * hueco de la pieza "desde dentro", que se lee como un agujero. Para que la
 * superficie cortada salga rellena (una sección técnica, como en un CAD) hace
 * falta el truco clásico del stencil buffer:
 *
 *   1. Por cada malla se añaden dos meshes invisibles que NO escriben color,
 *      solo stencil: las caras traseras lo incrementan y las frontales lo
 *      decrementan. Donde el plano atraviesa material sólido las cuentas no
 *      se cancelan y el stencil queda distinto de cero.
 *   2. Un plano ("cap") se dibuja después, encima, pintando solo donde el
 *      stencil quedó distinto de cero — es decir, exactamente la silueta del
 *      material cortado de esa malla.
 *
 * Hay un cap POR PIEZA, cada uno del color de su malla, para que al cortar un
 * diseño con varios dibujos cada sección salga de su color y se sepa qué es
 * cada cosa. Eso obliga a intercalar stencil y cap pieza a pieza (ver
 * `renderOrder` abajo) y a limpiar el stencil después de cada cap, o el de una
 * pieza teñiría a la siguiente.
 *
 * Los meshes de stencil se cuelgan como HIJOS de la malla que copian: así
 * heredan gratis su transformación (que cambia al abrir/separar las piezas) y
 * su visibilidad — ocultar una pieza deja de escribir su stencil y, con él,
 * desaparece su cap, sin sincronizar nada a mano.
 */

import * as THREE from 'three';

// El GLB usa 1 unidad = 1 cm, igual que en `partsLayout.js`.
const CM_PER_UNIT = 1;

/** Qué mitad se conserva al cortar. */
export const KEEP_BOTTOM = 'bottom';
export const KEEP_TOP = 'top';

// Color de reserva para la superficie de corte de una malla sin color propio.
const FALLBACK_CAP_COLOR = 0x8a8f98;

// El cap tiene que tapar la sección aunque las piezas estén separadas, así que
// se hace bastante más grande que el propio modelo.
const CAP_SIZE_FACTOR = 6;
const CAP_MIN_SIZE_UNITS = 20;

// Las mallas normales van en renderOrder 0. A partir de ahí se intercala, por
// cada pieza, su stencil y su cap.
const FIRST_RENDER_ORDER = 1;

const STENCIL_FLAG = 'customiz3rSectionStencil';

// El corte nunca se deja en el borde exacto de la pieza. Justo ahí el plano
// coincide con su cara y pasan las dos cosas que rompen la vista: por un lado
// la mitad conservada se queda vacía (a 0% no hay nada por debajo de la base) y
// solo queda el cap flotando; por otro el cap y la cara pelean por el mismo
// píxel y sale una mezcla de ambos colores. Se reserva una lámina a cada lado
// para que los extremos del slider enseñen "casi toda"/"casi nada" en vez de
// romperse.
const EDGE_MARGIN_RATIO = 0.01;

/**
 * El plano de corte, en coordenadas de mundo.
 *
 * Three.js descarta el semiespacio negativo del plano, o sea que conserva los
 * puntos donde `normal · punto + constant > 0`.
 */
export function createSectionPlane() {
    return new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
}

/**
 * Coloca el plano a una altura dada.
 *
 * `heightUnits` se mide desde el suelo del visor (y = 0), que es justo donde
 * `placeOnGround` apoya la base del modelo.
 */
export function setSectionPlane(plane, { heightUnits = 0, keep = KEEP_BOTTOM } = {}) {
    if (keep === KEEP_TOP) {
        // Conservar y > h  ⇒  y - h > 0
        plane.normal.set(0, 1, 0);
        plane.constant = -heightUnits;
    } else {
        // Conservar y < h  ⇒  h - y > 0
        plane.normal.set(0, -1, 0);
        plane.constant = heightUnits;
    }
}

/** Altura de corte, en unidades del visor, a partir de un porcentaje del alto. */
export function sectionHeightUnits(modelHeightUnits, percent) {
    const height = Number(modelHeightUnits) || 0;
    if (height <= 0) return 0;

    const ratio = Math.min(Math.max(Number(percent) || 0, 0), 100) / 100;
    const safeRatio = Math.min(Math.max(ratio, EDGE_MARGIN_RATIO), 1 - EDGE_MARGIN_RATIO);
    return height * safeRatio;
}

/** La misma altura en mm, para poder enseñarla junto al control. */
export function sectionHeightMm(modelHeightUnits, percent) {
    return sectionHeightUnits(modelHeightUnits, percent) * CM_PER_UNIT * 10;
}

/** El color con el que se pinta una malla, para teñir su superficie de corte. */
function meshColor(mesh) {
    const material = Array.isArray(mesh?.material) ? mesh.material[0] : mesh?.material;
    return material?.color ?? null;
}

/**
 * Cuelga de `mesh` los dos meshes que escriben el stencil. Idempotente: si ya
 * los tiene, no duplica.
 */
function attachStencilMeshes(mesh, plane, renderOrder) {
    if (!mesh?.geometry) return;
    if (mesh.children.some((child) => child.userData?.[STENCIL_FLAG])) return;

    const sides = [
        { side: THREE.BackSide, op: THREE.IncrementWrapStencilOp },
        { side: THREE.FrontSide, op: THREE.DecrementWrapStencilOp },
    ];

    sides.forEach(({ side, op }) => {
        const material = new THREE.MeshBasicMaterial();
        material.depthWrite = false;
        material.depthTest = false;
        material.colorWrite = false;
        material.stencilWrite = true;
        material.stencilFunc = THREE.AlwaysStencilFunc;
        material.side = side;
        material.clippingPlanes = [plane];
        material.stencilFail = op;
        material.stencilZFail = op;
        material.stencilZPass = op;

        // Geometría compartida con la malla original a propósito: solo se
        // dispone el material al desmontar (ver `detachStencilMeshes`), nunca
        // la geometría, que sigue siendo de la pieza.
        const stencilMesh = new THREE.Mesh(mesh.geometry, material);
        stencilMesh.renderOrder = renderOrder;
        stencilMesh.userData[STENCIL_FLAG] = true;
        mesh.add(stencilMesh);
    });
}

/** Quita los meshes de stencil de `mesh` y libera sus materiales. */
function detachStencilMeshes(mesh) {
    if (!mesh) return;
    mesh.children
        .filter((child) => child.userData?.[STENCIL_FLAG])
        .forEach((child) => {
            mesh.remove(child);
            // La geometría es la de la pieza: disponerla dejaría la malla real
            // sin nada que pintar.
            if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose());
            else child.material?.dispose();
        });
}

/**
 * El plano que rellena la superficie cortada de UNA pieza.
 *
 * `DoubleSide` para que se vea igual orbitando por encima o por debajo, y
 * `clearStencil()` después de pintarlo para que la pieza siguiente empiece con
 * el buffer limpio.
 *
 * `depthWrite: false` deja que, donde dos piezas se interpenetran (un dibujo
 * embebido en la base), gane el cap que se dibuja después en vez de que ambos
 * peleen por el píxel; de ahí que los dibujos se ordenen los últimos.
 */
function createCapMesh(sourceMesh, renderOrder) {
    const color = meshColor(sourceMesh);
    const material = new THREE.MeshStandardMaterial({
        color: color ? color.clone() : new THREE.Color(FALLBACK_CAP_COLOR),
        metalness: 0.0,
        roughness: 0.85,
        side: THREE.DoubleSide,
        depthWrite: false,
        stencilWrite: true,
        stencilRef: 0,
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.ReplaceStencilOp,
        stencilZFail: THREE.ReplaceStencilOp,
        stencilZPass: THREE.ReplaceStencilOp,
    });

    const cap = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
    cap.rotation.x = -Math.PI / 2; // el PlaneGeometry nace vertical
    cap.renderOrder = renderOrder;
    cap.userData.sourceMesh = sourceMesh;
    cap.onAfterRender = (renderer) => renderer.clearStencil();
    return cap;
}

/**
 * Orden en el que se seccionan las piezas.
 *
 * Los dibujos van los últimos para que, donde estén embebidos en la base, su
 * cap se dibuje encima del de ella y la sección salga del color del dibujo —
 * que es justo lo que se quiere distinguir al cortar.
 */
function sectionOrder(meshes) {
    const isDrawing = (mesh) => String(mesh?.name || '').startsWith('drawing_');
    return [...meshes].sort((a, b) => Number(isDrawing(a)) - Number(isDrawing(b)));
}

/**
 * Monta el corte sobre las mallas del modelo: plano en los materiales + stencil
 * + un cap por pieza. Devuelve el grupo de caps, que se mueve en bloque a la
 * altura del corte.
 */
export function enableSection(scene, meshes = [], plane, { modelSize } = {}) {
    const caps = new THREE.Group();

    sectionOrder(meshes).forEach((mesh, index) => {
        // Intercalado: stencil de la pieza, su cap, y vuelta a empezar. Sin
        // esto el stencil de una pieza teñiría el cap de la siguiente.
        const stencilOrder = FIRST_RENDER_ORDER + index * 2;
        applyPlaneToMaterial(mesh, plane);
        attachStencilMeshes(mesh, plane, stencilOrder);
        caps.add(createCapMesh(mesh, stencilOrder + 1));
    });

    resizeCaps(caps, modelSize);
    scene.add(caps);
    return caps;
}

/** Deshace `enableSection`: quita el plano de los materiales, el stencil y los caps. */
export function disableSection(scene, meshes = [], caps) {
    meshes.forEach((mesh) => {
        applyPlaneToMaterial(mesh, null);
        detachStencilMeshes(mesh);
    });

    if (caps) {
        scene.remove(caps);
        caps.children.forEach((cap) => {
            cap.geometry.dispose();
            cap.material.dispose();
        });
        caps.clear();
    }
}

/** Sube o baja las superficies de corte a la altura del plano. */
export function moveCaps(caps, heightUnits) {
    if (caps) caps.position.y = heightUnits;
}

/** Ajusta el tamaño de los caps al del modelo, con margen para las piezas separadas. */
export function resizeCaps(caps, modelSize) {
    if (!caps) return;
    const span = Math.max(modelSize?.x || 0, modelSize?.z || 0);
    const size = Math.max(span * CAP_SIZE_FACTOR, CAP_MIN_SIZE_UNITS);
    caps.children.forEach((cap) => cap.scale.set(size, size, 1));
}

/**
 * Vuelve a leer el color de cada pieza. Se llama al repintar una pieza desde el
 * panel de colores: sin esto su sección se quedaría del color anterior.
 */
export function syncCapColors(caps) {
    if (!caps) return;
    caps.children.forEach((cap) => {
        const color = meshColor(cap.userData?.sourceMesh);
        if (color) cap.material.color.copy(color);
    });
}

/**
 * Pone (o quita, con `null`) el plano de corte en los materiales de una malla.
 *
 * `clippingPlanes` es por material, no global, para que el corte afecte solo al
 * modelo: el grid del suelo se sigue viendo entero.
 */
function applyPlaneToMaterial(mesh, plane) {
    if (!mesh?.material) return;
    const planes = plane ? [plane] : null;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach((material) => {
        material.clippingPlanes = planes;
        material.needsUpdate = true;
    });
}
